import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useFormValidation } from '../hooks/useFormValidation';
import ClientFormFields, { emptyClientForm } from '../components/ClientFormFields';
import DataTable from '../components/DataTable';
import { EditableCell } from '../components/InlineEditTable';
import ReparentDeleteModal from '../components/ReparentDeleteModal';
import { useSoftDelete } from '../hooks/useSoftDelete';
import useSort from '../hooks/useSort';
import Toast from '../components/Toast';
import { useCurrentUser } from '../context/UserContext';

const API = process.env.REACT_APP_API_URL;

const emptyForm = emptyClientForm;
const emptyContactDraft = () => ({ name: '', email: '', phone: '' });

const CLIENT_RULES = {
  name: { required: true, label: 'Name' },
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(emptyForm());
  // Creating a client is admin-only (POST /clients/, tightened 2026-08-25 alongside
  // the admin-only program wizard). Finance keeps edit rights, so this one shared
  // form still opens — just never in its create mode.
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const [editing, setEditing] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);  // { client, programs } being deleted-with-reparent
  const [deleteBusy, setDeleteBusy] = useState(false);
  const { errors, validate, reset: resetErrors } = useFormValidation(CLIENT_RULES);

  const [addingContactFor, setAddingContactFor] = useState(null); // client id
  const [contactDraft, setContactDraft] = useState(emptyContactDraft());
  const [editingContact, setEditingContact] = useState(null); // contact id

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = () => axios.get(`${API}/clients/`).then(r => setClients(r.data));

  const { toast, dismissToast, deleteWithUndo } = useSoftDelete('client', fetchClients);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(form)) return;
    const data = { ...form, agency_id: form.agency_id ? parseInt(form.agency_id) : null };
    if (editing) {
      await axios.put(`${API}/clients/${editing}`, data);
      setEditing(null);
    } else {
      await axios.post(`${API}/clients/`, data);
    }
    setForm(emptyForm());
    resetErrors();
    fetchClients();
  };

  const handleEdit = (client) => {
    setEditing(client.id);
    setForm({
      name: client.name,
      sales_person: client.sales_person || '',
      client_code: client.client_code || '',
      agency_id: client.agency_id || '',
    });
    resetErrors();
  };

  const handleDelete = async (client) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await deleteWithUndo(client, client.name || 'client', () => axios.delete(`${API}/clients/${client.id}`));
    } catch (err) {
      if (err.response?.status === 409) {
        const programs = await axios.get(`${API}/clients/${client.id}/programs`).then(r => r.data);
        setDeleteModal({ client, programs });
      } else {
        alert('Delete failed: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const doReparentDelete = async (moves) => {
    setDeleteBusy(true);
    try {
      const client = deleteModal.client;
      await deleteWithUndo(client, client.name || 'client', () => axios.post(`${API}/clients/${client.id}/delete-with-reparent`, { moves }));
      setDeleteModal(null);
    } catch (err) {
      alert('Move & delete failed: ' + (err.response?.data?.detail || err.message));
    } finally { setDeleteBusy(false); }
  };

  const agencyName = (id) => clients.find(c => c.id === id)?.name || '—';

  const startAddContact = (clientId) => {
    setAddingContactFor(clientId);
    setEditingContact(null);
    setContactDraft(emptyContactDraft());
  };

  const startEditContact = (clientId, contact) => {
    setAddingContactFor(clientId);
    setEditingContact(contact.id);
    setContactDraft({ name: contact.name, email: contact.email || '', phone: contact.phone || '' });
  };

  const cancelContactDraft = () => {
    setAddingContactFor(null);
    setEditingContact(null);
    setContactDraft(emptyContactDraft());
  };

  const submitContact = async (clientId) => {
    if (!contactDraft.name.trim()) return;
    if (editingContact) {
      await axios.put(`${API}/clients/${clientId}/contacts/${editingContact}`, contactDraft);
    } else {
      await axios.post(`${API}/clients/${clientId}/contacts`, contactDraft);
    }
    cancelContactDraft();
    fetchClients();
  };

  const removeContact = async (clientId, contactId) => {
    if (!window.confirm('Remove this contact?')) return;
    await axios.delete(`${API}/clients/${clientId}/contacts/${contactId}`);
    fetchClients();
  };

  const contactInputStyle = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', width: '100%', boxSizing: 'border-box' };

  const derivedCode = (c) => (c.name || 'XX').slice(0, 2).toUpperCase();

  // PUT /clients/{id} takes the full object (no partial patch) — merge the
  // edited field into the row's current values, then patch state in place.
  const saveField = async (client, patch) => {
    const payload = {
      name: client.name,
      sales_person: client.sales_person || null,
      client_code: client.client_code || null,
      agency_id: client.agency_id || null,
      ...patch,
    };
    await axios.put(`${API}/clients/${client.id}`, payload);
    setClients(prev => prev.map(c => c.id === client.id ? { ...c, ...patch } : c));
  };

  // Called here rather than at the top of the component: the accessors close over
  // agencyName/derivedCode, and the hook sorts eagerly at call time.
  const { sorted: sortedClients, field: sortField, dir: sortDir, toggle: handleSort } = useSort(clients, null, 'asc', {
    agency: c => c.agency_id ? agencyName(c.agency_id) : '',
    client_code: c => c.client_code || derivedCode(c),
  });

  const renderContacts = (c) => (
    <div>
      {(c.contacts || []).map(ct => (
        <div key={ct.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
          <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
            {ct.name}{ct.email && <span style={{ color: '#7f8c8d' }}> — {ct.email}</span>}{ct.phone && <span style={{ color: '#7f8c8d' }}> — {ct.phone}</span>}
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>
            <button onClick={() => startEditContact(c.id, ct)} style={{ padding: '1px 6px', marginRight: '4px', background: 'none', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>Edit</button>
            <button onClick={() => removeContact(c.id, ct.id)} style={{ padding: '1px 6px', background: 'none', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>✕</button>
          </span>
        </div>
      ))}
      {addingContactFor === c.id ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', padding: '6px', background: '#f9f9f9', borderRadius: '4px' }}>
          <input placeholder="Name" value={contactDraft.name} onChange={e => setContactDraft({ ...contactDraft, name: e.target.value })} style={contactInputStyle} autoFocus />
          <input placeholder="Email" value={contactDraft.email} onChange={e => setContactDraft({ ...contactDraft, email: e.target.value })} style={contactInputStyle} />
          <input placeholder="Phone" value={contactDraft.phone} onChange={e => setContactDraft({ ...contactDraft, phone: e.target.value })} style={contactInputStyle} />
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={() => submitContact(c.id)} style={{ flex: 1, padding: '4px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>Save</button>
            <button onClick={cancelContactDraft} style={{ flex: 1, padding: '4px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => startAddContact(c.id)} style={{ padding: '2px 8px', background: 'none', border: '1px dashed #3498db', color: '#3498db', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>+ Add contact</button>
      )}
    </div>
  );

  const columns = [
    { key: 'name', header: 'Name', width: 160, sortable: true, vAlign: 'top', render: c =>
        <EditableCell value={c.name} display={<strong style={{ color: '#2c3e50' }}>{c.name}</strong>}
          onSave={v => {
            const name = v.trim();
            if (!name) throw new Error('Name is required');
            return saveField(c, { name });
          }} /> },
    { key: 'contacts', header: 'Contacts', width: 280, vAlign: 'top', render: renderContacts },
    { key: 'sales_person', header: 'Sales Person', width: 130, sortable: true, vAlign: 'top', render: c =>
        <EditableCell value={c.sales_person}
          onSave={v => saveField(c, { sales_person: v.trim() || null })} /> },
    { key: 'client_code', header: 'Code', width: 90, sortable: true, vAlign: 'top', render: c =>
        <EditableCell value={c.client_code} placeholder={derivedCode(c)}
          display={c.client_code
            ? c.client_code
            : <span title="Auto-derived from the client name — set a code to override">{derivedCode(c)} <span style={{ color: '#95a5a6', fontSize: '11px' }}>auto</span></span>}
          onSave={v => {
            const code = v.trim().toUpperCase();
            if (code && !/^[A-Z0-9]{2}$/.test(code)) throw new Error('Code must be exactly 2 letters/digits (blank = auto)');
            return saveField(c, { client_code: code || null });
          }} /> },
    { key: 'agency', header: 'Agency', width: 140, sortable: true, vAlign: 'top', render: c =>
        <EditableCell type="select" value={c.agency_id}
          options={[{ value: '', label: 'None' }, ...clients.filter(x => x.id !== c.id).map(x => ({ value: x.id, label: x.name }))]}
          display={c.agency_id ? agencyName(c.agency_id) : undefined}
          onSave={v => saveField(c, { agency_id: v ? parseInt(v) : null })} /> },
    { key: 'actions', header: 'Actions', width: 130, vAlign: 'top', nowrap: true, render: c => (
        <>
          <button onClick={() => handleEdit(c)} style={{ marginRight: '8px', padding: '4px 12px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Edit</button>
          <button onClick={() => handleDelete(c)} style={{ padding: '4px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Delete</button>
        </>
      ) },
  ];

  return (
    <div>
      <h2>Clients</h2>
      {(isAdmin || editing) && (
      <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px' }}>{editing ? 'Edit Client' : 'New Client'}</h3>
        <div style={{ marginBottom: '16px' }}>
          <ClientFormFields
            form={form}
            onChange={(field, value) => setForm(f => ({ ...f, [field]: value }))}
            errors={errors}
            agencyOptions={clients.filter(c => !editing || c.id !== editing).map(c => ({ value: c.id, label: c.name }))}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="submit" style={{ padding: '8px 16px', background: editing ? '#27ae60' : '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            {editing ? 'Update' : 'Add Client'}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(emptyForm()); resetErrors(); }} style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </form>
      )}
      <DataTable
        rows={sortedClients}
        columns={columns}
        sort={{ field: sortField, dir: sortDir, onSort: handleSort }}
        emptyText="No clients yet."
      />
      {deleteModal && (
        <ReparentDeleteModal
          allowCascade={false}
          entityName={deleteModal.client.name || `Client #${deleteModal.client.id}`}
          parentNoun="client"
          childNoun="programs"
          items={deleteModal.programs.map(p => ({ id: p.id, name: p.program_name || `Program #${p.id}` }))}
          targets={clients.filter(x => x.id !== deleteModal.client.id).map(x => ({ value: x.id, label: x.name }))}
          childIdKey="program_id"
          targetIdKey="target_client_id"
          onMove={doReparentDelete}
          onClose={() => setDeleteModal(null)}
          busy={deleteBusy}
        />
      )}
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
